'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ArrowRight, Loader2, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface Props {
  onSwitchTab: () => void;
}

function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const map = [
    { score: 1, label: 'Weak', color: 'bg-negative' },
    { score: 2, label: 'Fair', color: 'bg-warning' },
    { score: 3, label: 'Good', color: 'bg-primary' },
    { score: 4, label: 'Strong', color: 'bg-positive' },
  ];
  return map[score - 1] ?? { score: 0, label: '', color: '' };
}

export default function SignupForm({ onSwitchTab }: Props) {
  const router = useRouter();
  const { signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>();

  const strength = getPasswordStrength(passwordValue);

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.fullName });
      setSentEmail(data.email);
      setEmailSent(true);
      toast.success('Account created! Check your email to verify.');
    } catch (error: any) {
      setIsLoading(false);
      toast.error(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="animate-slide-up flex flex-col items-center gap-6 py-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
          <Mail size={32} className="text-primary" />
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Verify your email</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            We sent a verification link to<br />
            <span className="text-foreground font-semibold">{sentEmail}</span>
          </p>
        </div>
        <div className="w-full bg-muted/40 border border-border rounded-xl p-4 flex items-start gap-3">
          <CheckCircle size={16} className="text-positive mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Click the link in your email to activate your Joede Wallet account. Check your spam folder if you don't see it.
          </p>
        </div>
        <button
          onClick={() => { setEmailSent(false); onSwitchTab(); }}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-all"
        >
          Go to Sign In
          <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Create your wallet</h2>
        <p className="text-muted-foreground text-sm">A unique wallet ID will be generated for you</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {/* Full name */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="signup-name">Full name</label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="Adaeze Okonkwo"
            className={`w-full px-4 py-3 rounded-xl bg-input border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.fullName ? 'border-negative' : 'border-border'}`}
            {...register('fullName', {
              required: 'Full name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' },
            })}
          />
          {errors.fullName && <p className="text-negative text-xs mt-0.5">{errors.fullName.message}</p>}
        </div>

        {/* Email */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="signup-email">Email address</label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`w-full px-4 py-3 rounded-xl bg-input border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.email ? 'border-negative' : 'border-border'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && <p className="text-negative text-xs mt-0.5">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="signup-password">Password</label>
          <p className="text-muted-foreground text-xs">Min 8 characters with uppercase, number, and symbol</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              className={`w-full px-4 py-3 pr-11 rounded-xl bg-input border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.password ? 'border-negative' : 'border-border'}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'At least 8 characters required' },
                onChange: (e) => setPasswordValue(e.target.value),
              })}
            />
            <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle password">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {passwordValue && (
            <div className="flex items-center gap-2 mt-1">
              <div className="flex gap-1 flex-1">
                {[1, 2, 3, 4].map((i) => (
                  <div key={`strength-bar-${i}`} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= strength.score ? strength.color : 'bg-muted'}`} />
                ))}
              </div>
              <span className={`text-xs font-medium ${strength.score <= 1 ? 'text-negative' : strength.score === 2 ? 'text-warning' : strength.score === 3 ? 'text-primary' : 'text-positive'}`}>{strength.label}</span>
            </div>
          )}
          {errors.password && <p className="text-negative text-xs mt-0.5">{errors.password.message}</p>}
        </div>

        {/* Confirm password */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="signup-confirm">Confirm password</label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              className={`w-full px-4 py-3 pr-11 rounded-xl bg-input border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.confirmPassword ? 'border-negative' : 'border-border'}`}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (val) => val === watch('password') || 'Passwords do not match',
              })}
            />
            <button type="button" onClick={() => setShowConfirm((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle confirm password">
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-negative text-xs mt-0.5">{errors.confirmPassword.message}</p>}
        </div>

        {/* Terms */}
        <div className="flex flex-col gap-1">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" className="mt-0.5 w-4 h-4 rounded border-border bg-input accent-primary shrink-0" {...register('agreeTerms', { required: 'You must agree to the terms' })} />
            <span className="text-sm text-muted-foreground leading-snug">
              I agree to the{' '}
              <button type="button" className="text-primary hover:text-primary/80 transition-colors font-medium">Terms of Service</button>{' '}
              and{' '}
              <button type="button" className="text-primary hover:text-primary/80 transition-colors font-medium">Privacy Policy</button>
            </span>
          </label>
          {errors.agreeTerms && <p className="text-negative text-xs mt-0.5">{errors.agreeTerms.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? <Loader2 size={18} className="animate-spin" /> : (<>Create Wallet <ArrowRight size={16} /></>)}
        </button>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <button type="button" onClick={onSwitchTab} className="text-primary font-medium hover:text-primary/80 transition-colors">Sign in</button>
        </p>
      </form>
    </div>
  );
}